import {
  Box,
  Card,
  Typography,
  Alert,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
  MenuItem,
  FormHelperText
} from '@mui/material';
import { useEffect, useState } from 'react';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'src/redux-store/appStore';
import { httpRequest } from 'src/services/httpRequest';
import LoadingOverlay from 'src/components/common/LoadingOverlay';
import toast from 'react-hot-toast';
import { handleError } from 'src/redux-store/slices/errorSlice';
import UCCFeeConfiguration from './UCCFeeConfiguration';
import {
  UCCBuildingFee,
  UCCElectricalFee,
  UCCFireProtectionFee,
  UCCMechanicalFee,
  UCCPlumbingFee
} from './UCCWorkItemFee';

const FinanceConfiguration: React.FC = () => {
  const dispatch = useDispatch();
  const userDetails = useSelector((store: RootState) => store.user.userDetails);
  const [feesData, setFeesData] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editedFee, setEditedFee] = useState({ feeType: '', newFee: 0 });
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [activeEditFeeType, setActiveEditFeeType] = useState<string | null>(
    null
  );
  const [permitOptions, setPermitOptions] = useState([]);
  const [selectedPermit, setSelectedPermit] = useState<string | number>('');
  const [confirmUpdateTriggered, setConfirmUpdateTriggered] = useState(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [selectedUCC, setSelectedUCC] = useState<string>('BUILDING');
  const UCCTypes = [
    'BUILDING',
    'ELECTRICAL',
    'PLUMBING',
    'FIRE PROTECTION',
    'MECHANICAL',
    // 'CHIMNEY'
  ];

  // Extract permit names for the finance manager role
  useEffect(() => {
    if (userDetails && userDetails.TownshipPermits) {
      let permits = [];
      if (userDetails.UserRole === 'Admin') {
        // Admin: Show all permits
        permits = userDetails.TownshipPermits.flatMap((department) =>
          department.Permits.map((permit) => ({
            PermitID: permit.PermitID,
            PermitName: permit.PermitName
          }))
        );
      } else {
        // Finance Manager: Filter permits by UserRoleName
        permits = userDetails.TownshipPermits.flatMap((department) =>
          department.Permits.filter(
            (permit) => permit.UserRoleName === 'Finance Manager'
          )
        ).map((permit) => ({
          PermitID: permit.PermitID,
          PermitName: permit.PermitName
        }));
      }
      setPermitOptions(permits);
      if (permits.length > 0) {
        setSelectedPermit(permits[0].PermitID); // Set the first permit as the default value
      }
    }
  }, [userDetails]);

  // Fetch fees details for the selected permit
  useEffect(() => {
    if (selectedPermit) {
      const fetchFeesDetails = async () => {
        const feesResponse = await httpRequest(
          'GET',
          `/configuration/fee_details?PermitID=${selectedPermit}`
        );
        if (feesResponse?.status === 'success') {
          // Initialize an array to store the transformed data
          if (
            Array.isArray(feesResponse?.data) &&
            feesResponse?.data.length > 0
          ) {
            const setFeesDataArray = feesResponse?.data.map((item) => {
              // Convert the InsertDate to Date object
              const date = new Date(item.InsertDate);

              // Format the date as mm/dd/yyyy
              const formattedDate =
                (date.getMonth() + 1).toString().padStart(2, '0') +
                '/' +
                date.getDate().toString().padStart(2, '0') +
                '/' +
                date.getFullYear();

              return {
                feeType: item.FeeTypeName, // Map key to feeType using the mapping
                currentFee: item?.FeeTypeName === 'Credit Card Fee' ? `${item.FeeValue}%` : `$${item.FeeValue}`, // Convert the string to a number
                lastUpdated: formattedDate, // Formatted date for last updated
                editable: false
              };
            });
            setFeesData(setFeesDataArray); // Set the fee data to the state or variable
          } else {
            // If no fees data found (either empty object or no data)
            setFeesData([]); // Set an empty array or handle as needed
          }
        } else if (feesResponse.type === 'NETWORK_ERROR') {
        } else {
          toast.error(feesResponse?.message);
          dispatch(handleError(feesResponse?.message));
        }
      };
      fetchFeesDetails();
      setConfirmUpdateTriggered(false);
    }
  }, [selectedPermit, confirmUpdateTriggered]);

  const [error, setError] = useState('');

  const handleEditClick = (feeType: string, currentFee: number) => {
    setError('');
    setActiveEditFeeType((prev) => (prev === feeType ? null : feeType));
    setEditedFee({ feeType, newFee: currentFee }); // Reset the new fee
    setFeesData((prev) =>
      prev.map((fee) => ({
        ...fee,
        editable: fee.feeType === feeType
      }))
    );
  };

  const handleCancelEdit = () => {
    setActiveEditFeeType(null); // Clear active edit state
    setError('');
    setFeesData((prev) =>
      prev.map((fee) => ({
        ...fee,
        editable: false // Revert all rows to non-editable
      }))
    );
  };

  const handleFeeInputChange = (feeType: string, inputValue: number) => {
    const validateFee = (value: number, pattern: string) => {
      const decimalValue = value.toString();
      if (new RegExp(pattern).test(decimalValue)) {
        const parsedFee = parseFloat(decimalValue);
        // Ensure that parsedFee is a valid number before updating the state
        if (!isNaN(parsedFee)) {
          setEditedFee({ feeType, newFee: parsedFee }); // Update state with a number
        }
      }
    };

    // Define the validation pattern based on fee type
    if (feeType === 'Credit Card Fee') {
      validateFee(inputValue, '^\\d{0,2}(\\.\\d{0,2})?$'); // Credit Card Fee has a different pattern
    } else {
      validateFee(inputValue, '^\\d{0,4}(\\.\\d{0,2})?$'); // Other fee types
    }
  };

  const handleConfirmUpdate = async () => {
    const payload = {
      FeeType: editedFee.feeType,
      FeeValue: editedFee.newFee
    };
    setIsLoading(true);
    const response = await httpRequest(
      'POST',
      `/configuration/${selectedPermit}/fee_details`,
      payload
    );

    if (response?.status === 'success') {
      setConfirmUpdateTriggered(true);
      toast.success(response?.message);
    } else if (response.type === 'NETWORK_ERROR') {
    } else {
      toast.error(response?.message);
      dispatch(handleError(response?.message));
    }
    setIsLoading(false);

    setFeesData((prev) =>
      prev.map((fee) =>
        fee.feeType === editedFee.feeType
          ? {
            ...fee,
            currentFee: editedFee.newFee,
            lastUpdated: new Date().toISOString().split('T')[0],
            editable: false
          }
          : fee
      )
    );
    setDialogOpen(false);
    setActiveEditFeeType(null); // Reset the active edit field
  };

  useEffect(() => { }, [selectedPermit]);

  function handleUCCFee(getId) {
    if (selectedUCC === 'BUILDING') {
      return getId ? 1 : UCCBuildingFee;
    } else if (selectedUCC === 'ELECTRICAL') {
      return getId ? 2 : UCCElectricalFee;
    } else if (selectedUCC === 'PLUMBING') {
      return getId ? 3 : UCCPlumbingFee;
    } else if (selectedUCC === 'FIRE PROTECTION') {
      return getId ? 4 : UCCFireProtectionFee;
    } else if (selectedUCC === 'MECHANICAL') {
      return getId ? 5 : UCCMechanicalFee;
    } else if (selectedUCC === 'CHIMNEY') {
      return getId ? 7 : [];
    } else {
      return getId ? 0 : [];
    }
  }

  return (
    <>
      {isLoading && <LoadingOverlay />}
      <Card>
        <Box m={3}>
          <Alert severity="warning" sx={{ mb: 2 }}>
            You are about to update the application fee, credit card charges,
            and inspection fee settings. Kindly verify that all values are
            correct before proceeding, as these changes will directly impact
            financial transactions.
          </Alert>
          <Typography variant="h3" mb={3} color={'#6E759F'}>
            Permit finance configuration
          </Typography>

          {/* Permit Type Dropdown */}
          <Grid item xs={12} sm={6} mb={3}>
            <TextField
              select
              label="Permit Type"
              name="permit-type-select"
              value={selectedPermit}
              onChange={(e) => setSelectedPermit(e.target.value)}
              fullWidth
            >
              {permitOptions.map((permit) => (
                <MenuItem key={permit.PermitID} value={permit.PermitID}>
                  {permit.PermitName}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          {/* Fees Table */}
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow
                  sx={{
                    backgroundColor: (theme) => theme.palette.primary.main
                  }}
                >
                  <TableCell
                    align="center"
                    style={{
                      padding: '8px',
                      color: 'white',
                      borderTopLeftRadius: '10px',
                      textTransform: 'capitalize'
                    }}
                  >
                    Fee Type
                  </TableCell>
                  <TableCell
                    align="center"
                    style={{
                      padding: '8px',
                      color: 'white',
                      textTransform: 'capitalize'
                    }}
                  >
                    Current Fees
                  </TableCell>
                  <TableCell
                    align="center"
                    style={{
                      padding: '8px',
                      color: 'white',
                      textTransform: 'capitalize'
                    }}
                  >
                    Last Updated
                  </TableCell>
                  <TableCell
                    align="center"
                    style={{
                      padding: '8px',
                      color: 'white',
                      borderTopRightRadius: '10px',
                      textTransform: 'capitalize'
                    }}
                  >
                    Change Current Fees
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {feesData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      No data available
                    </TableCell>
                  </TableRow>
                ) : (
                  feesData.map((fee) => (
                    <TableRow key={fee.feeType}>
                      <TableCell align="center">{fee.feeType}</TableCell>
                      <TableCell align="center">
                        {fee.editable ? (
                          <>
                            <TextField
                              type="number"
                              value={editedFee.newFee}
                              onChange={(e) =>
                                handleFeeInputChange(
                                  fee.feeType,
                                  Number(e.target.value)
                                )
                              }
                              error={!!error} // Set error state to TextField
                            />
                            {error && (
                              <FormHelperText error>{error}</FormHelperText> // Display error message if validation fails
                            )}
                          </>
                        ) : (
                          fee.currentFee
                        )}
                      </TableCell>
                      <TableCell align="center">{fee.lastUpdated}</TableCell>
                      <TableCell align="center">
                        {fee.editable ? (
                          <>
                            <IconButton
                              onClick={() =>
                                editedFee.newFee > 0
                                  ? setDialogOpen(true)
                                  : null
                              }
                              disabled={error !== '' || editedFee.newFee <= 0}
                              sx={{
                                color:
                                  editedFee.newFee > 0 ? 'green' : 'default'
                              }}
                            >
                              <CheckIcon />
                            </IconButton>
                            <IconButton
                              onClick={() => handleCancelEdit()}
                              sx={{ color: '#8B0000' }}
                            >
                              <CloseIcon />
                            </IconButton>
                          </>
                        ) : (
                          <IconButton
                            color="primary"
                            onClick={() =>
                              handleEditClick(fee.feeType, fee.currentFee)
                            }
                          >
                            <EditIcon />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Confirmation Dialog */}
          <Dialog
            open={dialogOpen}
            onClose={() => setDialogOpen(false)}
            aria-labelledby="confirm-dialog-title"
          >
            <DialogTitle id="confirm-dialog-title">
              Confirm Fee Update
            </DialogTitle>
            <DialogContent>
              <DialogContentText>
                Are you sure you want to update the fee for "{editedFee.feeType}
                " to ${editedFee.newFee}?
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDialogOpen(false)} color="primary">
                Cancel
              </Button>
              <Button onClick={handleConfirmUpdate} color="primary">
                Confirm
              </Button>
            </DialogActions>
          </Dialog>

          {selectedPermit === 9 && (
            <Box mt={2}>
              <Typography variant="h4" mb={3} color={'#6E759F'}>
                UCC finance configuration
              </Typography>

              <Grid item xs={12} sm={6} mb={3}>
                <TextField
                  select
                  label="Select UCC Permit Type"
                  name="Select-UCC-Permit-Type"
                  value={selectedUCC}
                  onChange={(e) => setSelectedUCC(e.target.value)}
                  fullWidth
                >
                  {UCCTypes.map((permit) => (
                    <MenuItem key={permit} value={permit}>
                      {permit}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <UCCFeeConfiguration
                UCCFee={handleUCCFee(false)}
                getUCCId={handleUCCFee(true)}
              />
            </Box>
          )}
        </Box>
      </Card>
    </>
  );
};

export default FinanceConfiguration;
